import { falArrowRight } from 'shared/@generated/icons/falArrowRight';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { analytics } from 'shared/library/analytics';
import { withAttribution } from 'shared/library/attribution';
import { notify } from 'shared/library/notify';
import { useCurrentRoute } from 'shared/library/routes/route';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { BaseModal } from 'shared/components/base-modal';
import { Button } from 'shared/components/button';
import { TextInput } from 'shared/components/form/text-input';
import { Icon } from 'shared/components/icon';
import { TeamAvatars } from 'shared/components/team-avatars';
import { Text } from 'shared/components/text';

const feedbackPromptVersion = 6;

const ratingOptions = [
  {
    value: '1',
    color: '#FFA98D',
    calloutClassName: 'left-0 items-start',
    pointerOffsetClassName: 'ml-6',
    path: 'M12 14.6c1.48 0 2.9.38 4.15 1.1a.8.8 0 01-.79 1.39 6.76 6.76 0 00-6.72 0 .8.8 0 11-.8-1.4A8.36 8.36 0 0112 14.6zm4.6-6.25a1.62 1.62 0 01.58 1.51 1.6 1.6 0 11-2.92-1.13c.2-.04.25-.05.45-.08.21-.04.76-.11 1.12-.18.37-.07.46-.08.77-.12zm-9.2 0c.31.04.4.05.77.12.36.07.9.14 1.12.18.2.03.24.04.45.08a1.6 1.6 0 11-2.34-.38z',
  },
  {
    value: '2',
    color: '#FFC385',
    calloutClassName: 'left-1/4 ml-3 -translate-x-1/2 items-center',
    pointerOffsetClassName: '',
    path: 'M12 14.8c1.48 0 3.08.28 3.97.75a.8.8 0 11-.74 1.41A8.28 8.28 0 0012 16.4a9.7 9.7 0 00-3.33.61.8.8 0 11-.54-1.5c1.35-.48 2.56-.71 3.87-.71zM15.7 8c.25.31.28.34.51.64.24.3.25.3.43.52.18.23.27.33.56.7A1.6 1.6 0 1115.7 8zM8.32 8a1.6 1.6 0 011.21 2.73 1.6 1.6 0 01-2.7-.87c.28-.37.37-.47.55-.7.18-.22.2-.23.43-.52.23-.3.26-.33.51-.64z',
  },
  {
    value: '3',
    color: '#FFD885',
    calloutClassName: 'left-1/2 -translate-x-1/2 items-center',
    pointerOffsetClassName: '',
    path: 'M15.33 15.2a.8.8 0 01.7.66.85.85 0 01-.68.94h-6.2c-.24 0-.67-.26-.76-.7-.1-.38.17-.81.6-.9zm.35-7.2a1.6 1.6 0 011.5 1.86A1.6 1.6 0 1115.68 8zM8.32 8a1.6 1.6 0 011.21 2.73A1.6 1.6 0 118.33 8z',
  },
  {
    value: '4',
    color: '#FFEB85',
    calloutClassName: 'left-3/4 -ml-3 -translate-x-1/2 items-center',
    pointerOffsetClassName: '',
    path: 'M15.45 15.06a.8.8 0 11.8 1.38 8.36 8.36 0 01-8.5 0 .8.8 0 11.8-1.38 6.76 6.76 0 006.9 0zM15.68 8a1.6 1.6 0 011.5 1.86A1.6 1.6 0 1115.68 8zM8.32 8a1.6 1.6 0 011.21 2.73A1.6 1.6 0 118.33 8z',
  },
  {
    value: '5',
    color: '#CAE86F',
    calloutClassName: 'right-0 items-end',
    pointerOffsetClassName: 'mr-6',
    path: 'M16.8 14.4c.32 0 .59.2.72.45.12.25.11.56-.08.82a6.78 6.78 0 01-10.88 0 .78.78 0 01-.05-.87c.14-.23.37-.4.7-.4zM15.67 8a1.6 1.6 0 011.5 1.86A1.6 1.6 0 1115.68 8zM8.32 8a1.6 1.6 0 011.21 2.73A1.6 1.6 0 118.33 8z',
  },
] as const;

type RatingValue = (typeof ratingOptions)[number]['value'];

const promptConfigs = {
  listing_search: {
    questionId: 'listing_relevance',
  },
  listing_no_results: {
    questionId: 'listing_no_results',
  },
  breeds_index: {
    questionId: 'breed_comparison',
  },
  listing_detail: {
    questionId: 'listing_completeness',
  },
  breed_detail: {
    questionId: 'breed_detail_helpfulness',
  },
} as const;

export type ProductFeedbackPromptId = keyof typeof promptConfigs;
type FeedbackContextValue = string | number | boolean | null | undefined;
type FeedbackContext = Record<string, FeedbackContextValue>;

export function ProductFeedbackPrompt({
  promptId,
  context = {},
}: {
  promptId: ProductFeedbackPromptId;
  context?: FeedbackContext;
}) {
  const { t } = useTranslation('components');
  const currentRoute = useCurrentRoute();
  const modalRef = useRef<{ open: () => void; close: () => void }>(null);
  const cardRef = useRef<any>(null);
  const trackedView = useRef(false);
  const { request, loading } = useApi({ notifyOnServerErrors: false });
  const [primaryAnswer, setPrimaryAnswer] = useState<RatingValue | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [hidden, setHidden] = useState(false);
  const config = promptConfigs[promptId];
  const question = t(`product_feedback_prompt.prompts.${promptId}.question`, context);
  const submissionStorageKey = `product-feedback-submitted:${feedbackPromptVersion}:${promptId}`;
  const canSubmit = Boolean(primaryAnswer);
  const analyticsParams = useMemo(
    () => ({
      feedback_prompt_id: promptId,
      feedback_question_id: config.questionId,
      feedback_prompt_route: currentRoute.name,
      feedback_prompt_version: feedbackPromptVersion,
    }),
    [config.questionId, currentRoute.name, promptId],
  );

  useEffect(() => {
    if (hasRememberedPrompt(submissionStorageKey)) {
      setHidden(true);
    }
  }, [submissionStorageKey]);

  useEffect(() => {
    if (hidden || trackedView.current || !cardRef.current) return;

    const trackView = () => {
      if (trackedView.current) return;

      trackedView.current = true;
      analytics.event('product_feedback_prompt_view', analyticsParams);
    };

    if (typeof window.IntersectionObserver !== 'function') {
      trackView();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          trackView();
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(cardRef.current as Element);

    return () => observer.disconnect();
  }, [analyticsParams, hidden]);

  function openFeedback() {
    analytics.event('product_feedback_prompt_click', analyticsParams);
    modalRef.current?.open();
  }

  async function submitFeedback() {
    if (!primaryAnswer || !canSubmit || loading) return;

    const primaryAnswerLabel = t(`product_feedback_prompt.rating.options.${primaryAnswer}`);
    const cleanContext = Object.fromEntries(
      Object.entries(context).filter(([, value]) => value !== null && value !== undefined),
    );

    try {
      const payload = await withAttribution(
        {
          prompt_id: promptId,
          prompt_version: feedbackPromptVersion,
          question_id: config.questionId,
          question,
          primary_question: question,
          primary_answer: primaryAnswer,
          primary_answer_label: primaryAnswerLabel,
          secondary_question: null,
          secondary_answer: null,
          secondary_answer_label: null,
          comment: comment.trim() || null,
          route_name: currentRoute.name,
          page_url: window.location.href,
          context: cleanContext,
        },
        'product_feedback',
      );

      await request<{ success: boolean; feedback_id: number }>(
        apiRoute('product-feedback.store'),
        payload,
      );

      analytics.event('product_feedback_prompt_submit', analyticsParams);
      rememberPrompt(submissionStorageKey);
      setSubmitted(true);
      modalRef.current?.close();
    } catch {
      analytics.event('product_feedback_prompt_submit_error', analyticsParams);
      notify.error(
        {
          title: t('product_feedback_prompt.error_title'),
          description: t('product_feedback_prompt.error_description'),
        },
        'product-feedback-prompt.web.tsx - submitFeedback',
      );
    }
  }

  if (hidden) return null;

  if (submitted) {
    return (
      <PromptContainer cardRef={cardRef} promptId={promptId}>
        <View className={'flex-row items-center gap-3 p-3 md:p-4'}>
          <TeamAvatars />
          <Text className={'text-brand-brown-dark min-w-0 flex-1 text-base font-black'}>
            {t('product_feedback_prompt.success_title')}
          </Text>
        </View>
      </PromptContainer>
    );
  }

  return (
    <BaseModal
      ref={modalRef}
      closeOnBackdropPress={!loading}
      title={t('product_feedback_prompt.form_title')}
      footer={
        <Button
          text={t('product_feedback_prompt.submit')}
          center={true}
          loading={loading}
          disabled={!canSubmit || loading}
          onPress={() => void submitFeedback()}
          testID={`product-feedback-submit-${promptId}`}
        />
      }
      trigger={() => (
        <PromptContainer cardRef={cardRef} promptId={promptId}>
          <TouchableOpacity
            className={'p-4 md:hidden'}
            accessibilityRole={'button'}
            accessibilityLabel={`${t('product_feedback_prompt.intro')}. ${question}`}
            activeOpacity={0.82}
            onPress={openFeedback}
            testID={`product-feedback-prompt-action-${promptId}-mobile`}
          >
            <View className={'flex-row items-center'}>
              <TeamAvatars />
              <Text className={'text-brand-brown-dark/55 ml-3 min-w-0 flex-1 text-sm'}>
                {t('product_feedback_prompt.intro')}
              </Text>
            </View>

            <View className={'mt-3 block w-full text-left'}>
              <Text
                className={'text-brand-brown-dark inline text-left text-lg font-semibold leading-6'}
              >
                {question}
              </Text>
              <Icon
                icon={falArrowRight}
                className={'text-brand-brown-dark ml-1.5 inline-flex size-5 align-middle'}
              />
            </View>
          </TouchableOpacity>

          <View className={'hidden flex-row items-center gap-3 p-4 md:flex'}>
            <TeamAvatars />

            <TouchableOpacity
              className={'min-w-0 flex-1 items-start'}
              accessibilityRole={'button'}
              accessibilityLabel={`${t('product_feedback_prompt.intro')}. ${question}`}
              activeOpacity={0.82}
              onPress={openFeedback}
              testID={`product-feedback-prompt-action-${promptId}-desktop`}
            >
              <Text className={'text-brand-brown-dark/60 text-xs'}>
                {t('product_feedback_prompt.intro')}
              </Text>
              <View className={'mt-0.5 flex-row items-center'}>
                <Text
                  className={'text-brand-brown-dark shrink text-base font-black leading-5'}
                  numberOfLines={1}
                  ellipsizeMode={'tail'}
                >
                  {question}
                </Text>
                <Icon
                  icon={falArrowRight}
                  className={'text-brand-brown-dark ml-1.5 size-4 shrink-0'}
                />
              </View>
            </TouchableOpacity>
          </View>
        </PromptContainer>
      )}
    >
      <View testID={`product-feedback-modal-${promptId}`}>
        <FeedbackRating
          question={question}
          selected={primaryAnswer}
          promptId={promptId}
          onSelect={setPrimaryAnswer}
        />

        <View className={'mt-5'}>
          <Text className={'text-brand-brown-dark font-bold'}>
            {t('product_feedback_prompt.optional_comment')}
          </Text>
          <TextInput
            value={comment}
            onChangeText={setComment}
            multiline={true}
            className={'mt-2 min-h-20'}
            style={{ minHeight: 80, textAlignVertical: 'top' }}
            placeholder={t('product_feedback_prompt.comment_placeholder')}
            maxLength={2000}
            testID={`product-feedback-comment-${promptId}`}
          />
        </View>
      </View>
    </BaseModal>
  );
}

function PromptContainer({
  cardRef,
  children,
  promptId,
}: {
  cardRef: React.RefObject<any>;
  children: React.ReactNode;
  promptId: ProductFeedbackPromptId;
}) {
  return (
    <View
      ref={cardRef}
      className={
        'border-brand-brown-dark/15 shadow-brand-brown-dark/5 mx-auto w-full max-w-4xl overflow-hidden rounded-lg border bg-white shadow-sm md:shadow-none'
      }
      testID={`product-feedback-prompt-${promptId}`}
    >
      {children}
    </View>
  );
}

function FeedbackRating({
  question,
  selected,
  promptId,
  onSelect,
}: {
  question: string;
  selected: RatingValue | null;
  promptId: ProductFeedbackPromptId;
  onSelect: (value: RatingValue) => void;
}) {
  const { t } = useTranslation('components');
  const selectedOption = ratingOptions.find((option) => option.value === selected);

  return (
    <View className={'mt-5'}>
      <Text className={'text-brand-brown-dark font-bold'}>{question}</Text>
      <View className={'mx-auto mt-4 w-full max-w-sm'}>
        <View className={'relative'}>
          <View className={'bg-brand-brown-dark/10 absolute left-6 right-6 top-6 h-px'} />
          <View className={'flex-row justify-between'} accessibilityRole={'radiogroup'}>
            {ratingOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                className={clsx(
                  'size-12 items-center justify-center rounded-full bg-transparent p-1 transition-transform hover:scale-105 focus:outline-none focus-visible:scale-105',
                  selected && Number(option.value) > Number(selected) && 'grayscale',
                )}
                accessibilityRole={'radio'}
                accessibilityLabel={t(`product_feedback_prompt.rating.options.${option.value}`)}
                accessibilityState={{ checked: selected === option.value }}
                onPress={() => onSelect(option.value)}
                testID={`product-feedback-${promptId}-rating-${option.value}`}
              >
                <FeedbackRatingIcon color={option.color} path={option.path} />
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View className={'relative mt-2 h-8 justify-start'}>
          {selectedOption ? (
            <View className={clsx('absolute', selectedOption.calloutClassName)}>
              <View
                className={selectedOption.pointerOffsetClassName}
                style={{
                  width: 0,
                  height: 0,
                  borderLeftWidth: 5,
                  borderRightWidth: 5,
                  borderBottomWidth: 5,
                  borderLeftColor: 'transparent',
                  borderRightColor: 'transparent',
                  borderBottomColor: selectedOption.color,
                }}
              />
              <View
                className={'rounded-full px-2.5 py-1'}
                style={{ backgroundColor: selectedOption.color }}
              >
                <Text className={'text-brand-brown-dark text-xs font-bold'} numberOfLines={1}>
                  {t(`product_feedback_prompt.rating.options.${selectedOption.value}`)}
                </Text>
              </View>
            </View>
          ) : (
            <View className={'flex-row justify-between'}>
              <Text className={'text-brand-brown-dark/55 flex-1 text-xs'}>
                {t('product_feedback_prompt.rating.low')}
              </Text>
              <Text className={'text-brand-brown-dark/55 flex-1 text-right text-xs'}>
                {t('product_feedback_prompt.rating.high')}
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

function FeedbackRatingIcon({ color, path }: { color: string; path: string }) {
  return (
    <svg width={'100%'} height={'100%'} viewBox={'0 0 24 24'} focusable={false}>
      <circle fill={color} cx={12} cy={12} r={10} />
      <path fill={'#1E100B'} d={path} />
    </svg>
  );
}

function hasRememberedPrompt(storageKey: string) {
  try {
    return window.sessionStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

function rememberPrompt(storageKey: string) {
  try {
    window.sessionStorage.setItem(storageKey, '1');
  } catch {}
}
